import { useCallback } from "react";

import { Action, Category } from "@/constants/events";
import { useEventTracker } from "@/hooks/useEventTracker";

import Coin from "@/components/Coin";
import Placeholder, { Rect } from "@/components/Skeleton";
import TrackImpression from "@/components/TrackImpression";

type Props = {
  className?: string;
  coinData: { icon: string; label: string; value: string; url?: string }[];
  hrefLink?: string;
  hrefLinkQueryParam?: string;
  hrefProductCatQueryKey?: string;
  queryParamAsPath?: boolean;
  isLoading?: boolean;
  title?: string;
  impressionEventCategory?: string;
  clickEventCategory?: string;
  clickEventProps?: Record<string, string | number>;
};

const CoinList: React.FC<Props> = ({
  className,
  coinData,
  hrefLink = "/search/shop",
  hrefLinkQueryParam = "",
  hrefProductCatQueryKey = "product_category",
  impressionEventCategory,
  isLoading,
  title,
  clickEventCategory,
  clickEventProps,
  queryParamAsPath = false,
}) => {
  const { publishEvent } = useEventTracker();

  const Wrapper = useCallback(
    ({ children, label }: { children: React.ReactNode; label: string }) => {
      if (impressionEventCategory) {
        return (
          <TrackImpression
            data-testid="track-impression"
            category={impressionEventCategory}
            label={label}
          >
            {children}
          </TrackImpression>
        );
      } else {
        return <>{children}</>;
      }
    },
    [impressionEventCategory],
  );

  if (!isLoading && !coinData.length) {
    return <></>;
  }

  return (
    <div className={className}>
      {title && <h2 className="heading--m font-extrabold mb-md">{title}</h2>}
      <div className="overflow-x-auto no-scrollbar">
        <ul data-testid="coin-list" className="flex gap-lg">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <li
                  className="flex-shrink-0 mb-sm md:mb-none"
                  key={`category-coin-placeholder-${i}`}
                >
                  <Placeholder
                    className="w-20 h-20"
                    data-testid="category-coin-placeholder"
                  >
                    <Rect height="100%" width="100%" />
                  </Placeholder>
                </li>
              ))
            : coinData.map((category) => (
                <Wrapper key={category.label} label={category.label}>
                  <Coin
                    href={
                      category.url ||
                      (queryParamAsPath
                        ? `${hrefLink}/${category.value.toLowerCase()}`
                        : `${hrefLink}?${hrefProductCatQueryKey}=${category.value}${hrefLinkQueryParam}`)
                    }
                    onClick={() => {
                      publishEvent({
                        action: Action.click,
                        category:
                          clickEventCategory ||
                          Category.productCategoryCoinFilter,
                        label: category.label.toLowerCase().replace(/-/g, ""),
                        ...clickEventProps,
                      });
                    }}
                    title={category.label.toLowerCase()}
                    dataTestId={`${category.label}-products-category`}
                    iconFilePath={category.icon}
                  />
                </Wrapper>
              ))}
        </ul>
      </div>
    </div>
  );
};

export default CoinList;
